import { EconomicReadinessKPIs, FundingOrGrantRound } from '@/Domain/Analytics/analytics-types'
import { KPI } from '@/Domain/Shared/shared-types'
import { toAmount, transformNumberIntoFormattedCurrencyString } from '@/util/currency'
import { ChartData, ChartDataset } from '../analytics-types'

const CHART_COLORS = [
  '#4CAF50', // Green
  '#2196F3', // Blue
  '#FFC107', // Amber
  '#9C27B0', // Purple
  '#FF5722', // Deep Orange
  '#00BCD4', // Cyan
  '#FF9800', // Orange
  '#3F51B5', // Indigo
  '#E91E63', // Pink
  '#009688', // Teal
]

export const transformKPIsResponseToKPIArray = (response: any): EconomicReadinessKPIs => {
  const revenueValue = transformNumberIntoFormattedCurrencyString(response.revenueKPI.value)

  const revenueKPI: KPI = {
    name: response.revenueKPI.name,
    value: revenueValue,
    description: response.revenueKPI.description,
  }

  return {
    newActors: response.newActorsKPI,
    memberships: response.membershipKPI,
    employment: response.employmentKPI,
    revenue: revenueKPI,
  }
}

export const transformFundingOrGrantRoundListDataToFundingOrGrantRoundArray = (response: any): FundingOrGrantRound[] => {
  return response.map(
    (item: any) => ({
      actorName: item.actorName,
      elasticId: item.elasticId,
      logo: item.logo,
      type: item.type,
      capitalRaised: item.capitalRaised,
      currency: item.currency,
      date: item.date, // Assuming date is in YYYY-MM-DD format
      originalCapitalRaised: toAmount(item.originalCapitalRaised),
      originalCurrency: item.originalCurrency,
    })
  )
}

export const transformChartDataApiResponseToChartDataArray = (response: any): ChartData => {
  if (!response?.xAxisLabels || !response?.datasets) {
    return {
      labels: [],
      datasets: [],
    }
  }

  // Find the first index where any dataset has a non-zero value
  const firstNonZeroIndex = response.xAxisLabels.findIndex((_: string, index: number) => {
    return response.datasets.some((dataset: any) => dataset?.yAxisValues?.[index] > 0)
  })

  // If no non-zero values found, return empty data
  if (firstNonZeroIndex === -1) {
    return {
      labels: [],
      datasets: [],
    }
  }

  // Find the index where year 2000 starts, we assume the label is either "Q1 2024" or "2024" formatted
  const year2000Index = response.xAxisLabels.findIndex((label: string) => {
    // Handle both "Q1 2024" and "2024" formats
    const year = typeof label === 'string' && label.includes(' ') 
      ? parseInt(label.split(' ')[1]) // Extract year from "Q1 2024" format
      : parseInt(label) // Direct year format
    return year >= 2000
  })

  // Use the latter of the two starting points
  const startIndex = Math.max(firstNonZeroIndex, year2000Index)

  // Slice the data arrays from the determined start point
  const trimmedLabels = response.xAxisLabels.slice(startIndex)
  const trimmedDatasets = response.datasets.map((dataset: any, index: number) => ({
    label: dataset.label,
    data: dataset.yAxisValues?.slice(startIndex) || [],
    backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
    valueCounts: dataset.valueCounts?.slice(startIndex) || [],
  }))

  return {
    labels: trimmedLabels,
    datasets: trimmedDatasets,
  }
}
